import * as React from 'react';

import type {
  ColumnSizingInfoState,
  ColumnSizingState,
  TableState,
  Updater,
} from '@tanstack/react-table';

import { getFromLocalStorage, setToLocalStorage } from '@/utils/localStorage';

const COLUMN_SIZING_STORAGE_PREFIX = 'table-column-sizing';

const DEFAULT_SIZING_INFO: ColumnSizingInfoState = {
  startOffset: null,
  startSize: null,
  deltaOffset: null,
  deltaPercentage: null,
  isResizingColumn: false,
  columnSizingStart: [],
};

/**
 * Provides the column-sizing slice needed to make a plain `useReactTable` resizable, with
 * localStorage persistence — without the URL/pagination/sorting state of `useDataTable`.
 *
 * Two performance guards (copied from `useDataTable`) are essential when the table contains
 * Radix popovers (e.g. a MultiSelect cell): TanStack updates sizing/sizingInfo on every
 * pixel of drag. Letting that hit React state per pixel re-renders the whole table and
 * trips Radix PopperAnchor's "Maximum update depth exceeded". So:
 * - column sizing is committed via requestAnimationFrame (throttled), and
 * - sizing INFO is kept in a ref, with React state updated only when resizing starts/ends.
 *
 * Spread the returned `tableOptions` into `useReactTable` and call `saveColumnSizing` on
 * resize end (pass it as the table's `onResizeEnd`).
 *
 * @param tableId - unique key for this table's persisted widths; resizing is disabled when omitted.
 */
export function useResizableColumns(tableId: string | undefined) {
  const storageKey = tableId ? `${COLUMN_SIZING_STORAGE_PREFIX}-${tableId}` : null;

  const [columnSizing, setColumnSizing] = React.useState<ColumnSizingState>(() => {
    if (!storageKey) return {};
    return getFromLocalStorage<ColumnSizingState>(storageKey) ?? {};
  });

  const columnSizingRef = React.useRef(columnSizing);
  columnSizingRef.current = columnSizing;

  // Throttle live sizing updates to one commit per animation frame.
  const pendingSizingRef = React.useRef<ColumnSizingState | null>(null);
  const rafIdRef = React.useRef<number | null>(null);

  const throttledSetColumnSizing = React.useCallback(
    (updaterOrValue: Updater<ColumnSizingState>) => {
      pendingSizingRef.current =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(columnSizingRef.current)
          : updaterOrValue;

      if (rafIdRef.current === null) {
        rafIdRef.current = requestAnimationFrame(() => {
          if (pendingSizingRef.current !== null) {
            setColumnSizing(pendingSizingRef.current);
            columnSizingRef.current = pendingSizingRef.current;
            pendingSizingRef.current = null;
          }
          rafIdRef.current = null;
        });
      }
    },
    []
  );

  const [columnSizingInfo, setColumnSizingInfo] =
    React.useState<ColumnSizingInfoState>(DEFAULT_SIZING_INFO);
  const columnSizingInfoRef = React.useRef<ColumnSizingInfoState>(DEFAULT_SIZING_INFO);

  const handleColumnSizingInfoChange = React.useCallback(
    (updaterOrValue: Updater<ColumnSizingInfoState>) => {
      const next =
        typeof updaterOrValue === 'function'
          ? updaterOrValue(columnSizingInfoRef.current)
          : updaterOrValue;

      const wasResizing = columnSizingInfoRef.current.isResizingColumn;
      columnSizingInfoRef.current = next;

      // Re-render only when resizing starts or ends, not on every drag pixel.
      if (wasResizing !== next.isResizingColumn) {
        setColumnSizingInfo(next);
      }
    },
    []
  );

  React.useEffect(() => {
    return () => {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
      }
    };
  }, []);

  const saveColumnSizing = React.useCallback(() => {
    if (storageKey) {
      setToLocalStorage(storageKey, columnSizingRef.current);
    }
  }, [storageKey]);

  const tableOptions = {
    enableColumnResizing: !!tableId,
    columnResizeMode: 'onChange' as const,
    state: { columnSizing, columnSizingInfo } satisfies Partial<TableState>,
    onColumnSizingChange: throttledSetColumnSizing,
    onColumnSizingInfoChange: handleColumnSizingInfoChange,
  };

  return { tableOptions, saveColumnSizing };
}
